import React, { useState, useEffect, useRef } from 'react';
import { FiImage, FiUpload, FiSave, FiRefreshCw, FiExternalLink, FiX } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

const S = {
  label:    { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:    { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit' },
  btnGhost: { background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontWeight: 500, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: '#374151', fontFamily: 'inherit' },
  btnDanger:{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit' },
};

const uploadImage = async (file) => {
  const form = new FormData();
  form.append('image', file);
  const { data } = await API.post('/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!data.success) throw new Error(data.message || 'Upload failed');
  return data.url;
};

const AdminContactHero = () => {
  const [imgUrl,    setImgUrl]    = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [loading,   setLoading]   = useState(true);
  const fileRef = useRef();

  useEffect(() => {
    API.get('/site-settings/public/contact_hero')
      .then(({ data }) => setImgUrl(data.contact_hero?.heroImage || ''))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImgUrl(url);
      toast.success('Image uploaded — click Save to apply');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put('/site-settings/contact_hero', { heroImage: imgUrl });
      toast.success('Contact hero image saved');
    } catch {
      toast.error('Failed to save image');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <div className="spinner" />
      <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Loading…</p>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 4 }}>Contact Hero Image</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Manage the hero image shown on the Contact Us page.
        </p>
      </div>

      {/* Card */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        marginBottom: 28,
      }}>
        {/* Card header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid var(--border)',
          background: 'var(--bg-soft)',
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Contact Us Hero Image</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>
              Appears in the right column of the Contact Us hero section (top of page).
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !imgUrl}
            className="btn btn-primary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }}
          >
            {saving
              ? <FiRefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
              : <FiSave size={14} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {/* Card body */}
        <div style={{ padding: 24 }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

            {/* Preview box */}
            <div style={{
              width: 220, height: 148, borderRadius: 10, overflow: 'hidden',
              border: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {imgUrl
                ? <img src={imgUrl} alt="Contact hero preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ textAlign: 'center', color: '#d1d5db' }}>
                    <FiImage size={32} />
                    <div style={{ fontSize: 11, marginTop: 6 }}>No image set</div>
                  </div>
              }
            </div>

            {/* Controls */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>Image URL</label>
                <input
                  style={S.input}
                  value={imgUrl}
                  onChange={(e) => setImgUrl(e.target.value)}
                  placeholder="https://cdn.example.com/image.webp"
                />
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  style={S.btnGhost}
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? <FiRefreshCw size={13} /> : <FiUpload size={13} />}
                  {uploading ? 'Uploading…' : 'Upload Image'}
                </button>
                {imgUrl && (
                  <>
                    <a
                      href={imgUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ ...S.btnGhost, textDecoration: 'none' }}
                    >
                      <FiExternalLink size={13} /> Preview
                    </a>
                    <button style={S.btnDanger} onClick={() => setImgUrl('')}>
                      <FiX size={13} /> Clear
                    </button>
                  </>
                )}
              </div>

              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleFile}
              />

              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 12 }}>
                Recommended: 900×675px or larger (4:3 ratio). PNG or JPG. Max 5 MB.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Note */}
      <div style={{
        background: '#fffbeb', border: '1px solid #fde68a',
        borderRadius: 'var(--radius)', padding: '14px 18px',
        fontSize: '0.8rem', color: '#92400e',
      }}>
        <strong>Note:</strong> Only the hero image is admin-controlled here.
        All heading text, description, layout, and badges are part of the page design.
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminContactHero;